import {
  AgenticProcess,
  ComputeNode,
  CredentialsSubview,
  dataContext,
  DockPointerData,
  isHubOnly,
  type IDockPointer,
  Layout,
  PageId,
  QueryFilter,
  QueryRequest,
  Shell,
  tabManager,
  tabForDockKey,
  toplog,
  TypeId,
  VFSPath,
  viewModeMemory,
  ViewType,
} from '@sdk';
import { NavigateFunction } from 'react-router';
import { isValidIdentifier } from '@sdk/models/TypeId';
import {
  UNSTATED_VIEW_MODE,
  sessionOpenViewMode,
  sessionIdForDock,
  VIEW_MODE_SWITCH_STATE,
  ViewMode,
} from '@src/contexts/view-mode-context';
import {
  CAPABILITY_PARAM,
  DockPointer,
  JOURNEY_PARAM,
  JOURNEY_STEP_PARAM,
  NODE_PARAM,
  WORKSPACE_PARAM,
  WORKSPACE_ENTRY_PARAM,
} from './DockPointer';
import { dockPointerForFile } from './local-file-pointer';
import { getHistoryPosition } from './history-position-store';
import { beginTabSwitch, dockLabel, tabSwitch } from './tab-switch-state';
import { FileOptions, TabOptions } from './types';
import { preserveWindowLayout, stripDockPortion } from './url-builder';
import { allScope, projectScope } from '@src/lib/scope-filter';
import { contentAssetTargetForDock, isContentAssetDock, offersDocChat } from './content-asset-dock';
import { isAdoptableChildDock, isWorkspaceAnchorDock } from './adoptable-child-dock';
import { isHostDock } from './tab-hosts';
import { LOCAL_COMPUTE_NODE } from './asset-doc-types';
import { vfsLocatorForComputeNode } from './vfs-locator';
import { dockForDisplayTarget } from './display-target-pointer';
import { withHomePage } from '@src/project-home-page/home-page-state';
import { placeDockInProject, presentDockTab } from './present-dock-tab';
import { openExternal } from '@src/lib/open-external';
import { openInBrowserProfile } from '@src/lib/browser-profiles';
import { appLinkPath, bareWebUrl, isWebUrl, localWebUrl } from '@src/lib/link-kind';
import { errorMessage } from '@src/lib/error-message';
import type { LinkSource } from '@src/components/links/link-events';
import { notify } from '@src/notifications/notify';
import { t } from '@lingui/core/macro';

// Always returns a record (possibly empty) so consumers can read keys without
// optional-chaining. An earlier version returned `undefined` for empty input,
// which made every consumer responsible for `?.` — `openShellProcess` missed
// one and crashed on option-less opens (Quick Create from Home). `openDock`
// treats an empty record the same as no extra options.
function toStringRecord(obj?: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  if (!obj) return result;
  for (const [key, value] of Object.entries(obj)) {
    if (value == null || value === false || typeof value === 'object') continue;
    result[key] = typeof value === 'string' ? value : `${value as number | boolean}`;
  }
  return result;
}

/**
 * How a dock commit should enter browser history.
 *
 * Default is a PUSH, because a navigation is normally something the user chose and
 * Back should undo it. `replace` is for a commit the user did NOT initiate — the
 * agent re-pointing the vibe workspace's active display, or a loader restoring it —
 * where each push would otherwise cost the user one Back press to escape a history
 * they never asked for, and where a pushed entry would immediately redirect forward
 * again (the classic redirect trap).
 */
export interface NavigationCommitOptions {
  replace?: boolean;
  /**
   * The user switched view mode (the footer toggle). Travels as history state so
   * `useDockViewModeOverrideSync` can tell it from a redirect that merely adds a
   * mode to a bare URL — only a switch saves the preference.
   */
  viewModeSwitch?: boolean;
  /**
   * Open as a TOP-LEVEL tab — the global strip's chips and "+": never carried into
   * the Vibe workspace on screen as its child.
   */
  topLevel?: boolean;
  /** The `tab_switch` start line's `via=` when neither flag above names it. */
  via?: string;
}

interface PendingDockNavigation {
  /**
   * Where we are GOING. `navigate()` is async — React Router runs loaders before
   * the URL changes — so for that whole window `window.location` still reports
   * the PREVIOUS location. Anything that composes a URL from the live location
   * during that window and commits it supersedes the navigation in flight.
   *
   * Holding the destination as a POINTER is what makes that unrepresentable:
   * `here` returns this, so a write composes onto the destination instead of
   * rebuilding the location we already left.
   */
  target: DockPointer;
  targetUrl: string;
  sourceUrl: string;
}

let pendingDockNavigation: PendingDockNavigation | null = null;

// View types whose dock adopts the current project's scope when opened without an
// explicit one (see openDock). These are the project-aware browser surfaces: the
// minted Tab attaches to the active project (project tab) or stays global. SHELL
// is included so the Chats/worker rail seeds the active project's scope onto the
// URL — the ChatsNavigator reads currentDock.scopeFilter to filter history, exactly
// like assets/explorer/triggers (SHELL's tabHash ignores scope, so the open
// session's identity is unaffected).
/**
 * The project a session dock BELONGS to, when it is already known without a
 * fetch: its open tab's project, else the cached process's. A session opened
 * from another project (a project switch, a chat link, spotlight) must carry its
 * own project's scope — seeding the one being left made the loader's scope
 * reconcile redirect, so the whole loader ran a second time.
 */
function ownerProjectId(dock: DockPointer): string | null {
  if (!DockPointer.isSessionView(dock.viewType)) return null;
  const tab = tabForDockKey(tabManager.getSnapshot(), dock.tabHash);
  if (tab) return tab.project_id ?? null;
  const processId = sessionIdForDock(dock);
  // A malformed id (a hand-typed or legacy link) must not throw out of openDock.
  if (!processId || !isValidIdentifier(processId)) return null;
  return AgenticProcess.getByIdFromCache<AgenticProcess>(processId)?.project_id ?? null;
}

export const SCOPE_SEEDED_VIEWS: ReadonlySet<ViewType> = new Set([
  ViewType.ASSETS,
  ViewType.EXPLORER,
  ViewType.SCHEMA_TREE,
  ViewType.SHELL,
  ViewType.VIBE,
]);

// URL options that are STICKY across navigation: openDock carries each from the
// live URL onto any target that doesn't set it. A param here means "topmost
// until explicitly closed" — clearing it must bypass openDock (see closeJourney).
export const STICKY_OPTION_PARAMS: readonly string[] = [JOURNEY_PARAM, JOURNEY_STEP_PARAM, WORKSPACE_PARAM];

/**
 * The workspace host to carry from `here` onto `target`, or null.
 *
 * Both commit paths (`openDock` and `commitDetached`) apply this, so the rule is
 * stated once: content opened from inside a workspace stays in that workspace,
 * and a navigation AWAY — a process, project or list dock — drops it rather than
 * resurrecting a workspace around a top-level surface. `isAdoptableChildDock` is
 * the same predicate that decides whether a tab may adopt a parent at all.
 *
 * Carrying from the live URL, rather than reading the target's own tab row, is
 * what keeps a click inside workspace A in workspace A: one document is one tab
 * however many agents display it, so the row's `parent_tab_id` is
 * last-writer-wins and would teleport the user into whichever workspace showed
 * it most recently.
 */
function hostToCarry(here: DockPointer | null, target: DockPointer): string | null {
  if (!here || target.hostProcessId || !isAdoptableChildDock(target)) return null;
  return here.hostProcessId ?? hostOfWorkspaceAnchor(here);
}

/**
 * The host id when `dock` IS a vibe workspace's anchor — the process dock the
 * Display renders. The anchor cannot carry a `host` option itself (it is not an
 * adoptable child), so this is how content opened while sitting on the Display
 * inherits it — and that is where most opens actually start.
 *
 * Reading the ambient mode here is deliberate, and is NOT the thing
 * `canonicalWorkspaceDisplayPath` refuses to do. That runs in the LOADER, before
 * the mounted dock has settled the effective mode, so an ambient read there can
 * be wrong for a dock whose own mode differs from the ambient one. This runs
 * at click time, long after mount, when the effective mode is settled.
 */
/** An asset as a Vibe host's child: rebased onto its project, `?host=` the session. */
function hostedAssetDock(asset: DockPointer, projectId: string | null, processId: string): DockPointer {
  return DockPointer.rebaseAssetsOntoProject(asset, projectId)
    .withHost(new TypeId(AgenticProcess.type, processId).toString())
    .withViewMode(ViewMode.Vibe);
}

function hostOfWorkspaceAnchor(dock: DockPointer): string | null {
  if (!isWorkspaceAnchorDock(dock)) return null;
  // A host tab IS a workspace: what it opens is its child.
  if (isHostDock(dock)) return dock.pointer ?? null;
  if (dock.viewType !== ViewType.SHELL) return null;
  return (dock.viewMode ?? UNSTATED_VIEW_MODE) === ViewMode.Vibe ? (dock.pointer ?? null) : null;
}

/** A link that cannot be opened says why — the backend's sentence, not the HTTP status — once,
 *  as a toast that times out. It is about this click, so it never becomes a standing warning. */
function notifyLinkError(error: unknown): void {
  notify.error({ title: t`Could not open link`, message: errorMessage(error, t`Unknown error`), transient: true });
}

/**
 * NavigationActions - Navigation actions implementation
 *
 * Core principle: All actions update URL (URL-first architecture)
 * Navigation.openDock(pointer) is the basic method, all shortcuts call it
 *
 * Uses relative navigation: takes current URL and replaces the dock portion
 */

export class NavigationActions {
  constructor(
    private navigate: NavigateFunction,
    private currentDock: DockPointer | null = null,
  ) {
    NavigationActions.clearCommittedPendingNavigation();
  }

  private static getCurrentBrowserUrl(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  /**
   * WHERE THE APP IS, OR IS ABOUT TO BE — the pointer every URL write composes
   * onto.
   *
   * A navigation in flight wins over the browser's location, because the browser
   * has not moved yet (see {@link PendingDockNavigation.target}). Composing onto
   * the live location during that window is what once let a journey's
   * `?highlight=` write revert the navigation it had just asked for.
   *
   * Never null: the app root is an ordinary pointer now.
   */
  get here(): DockPointer {
    return pendingDockNavigation?.target ?? NavigationActions.currentBrowserDock();
  }

  /** The live browser URL as a pointer. Falls back to the root for a URL we
   *  cannot parse — the app is somewhere, and the root is the safe somewhere. */
  private static currentBrowserDock(): DockPointer {
    try {
      return DockPointer.fromUrl(NavigationActions.getCurrentBrowserUrl());
    } catch {
      return DockPointer.root();
    }
  }

  /** The `?viewMode` on the LIVE browser URL, or null. Used for view-mode
   *  stickiness seeding — always current, unlike the React-state `currentDock`
   *  mirror which lags during the first navigation after a hard page load. */
  private static currentBrowserViewMode(): ViewMode | null {
    try {
      return DockPointer.fromUrl(NavigationActions.getCurrentBrowserUrl()).viewMode;
    } catch {
      return null;
    }
  }

  /** State a mode-less target's mode. Only a session (and what it hosts) has
   *  one: its own remembered mode, else the last mode the user selected — never
   *  the mode on screen, which belongs to the tab being LEFT. A hosted child
   *  shows in its host's mode (`liveMode`). Every other page states none. */
  private static withTargetViewMode(target: DockPointer, liveMode: () => ViewMode | null): DockPointer {
    // A process asked for in Vibe by option is the Vibe host dock — map it here,
    // on the way out, so no opener ever writes the old spelling and pays the
    // loader's canonical redirect for it.
    if (target.viewType === ViewType.SHELL && target.viewMode === ViewMode.Vibe) {
      return target.withViewMode(ViewMode.Vibe);
    }
    if (target.viewMode !== null) return target;
    // Not in cache: opens bare and the shell loader states it from the entity.
    const session = sessionIdForDock(target) ? viewModeMemory.targetFor(target.targetTypeId) : null;
    const mode = session ? sessionOpenViewMode(session) : target.hostProcessId ? liveMode() : null;
    return mode ? target.withViewMode(mode) : target;
  }

  private static clearCommittedPendingNavigation(): void {
    if (!pendingDockNavigation) return;
    const currentUrl = NavigationActions.getCurrentBrowserUrl();
    // A loader may redirect the requested URL to a canonical destination (for
    // example bare /dock/shell → a concrete, project-scoped shell). Either an
    // exact target commit or any departure from the stamped source proves the
    // transition finished; retaining the raw requested URL after a redirect
    // suppresses the next legitimate click until the fallback timer fires.
    if (currentUrl === pendingDockNavigation.targetUrl || currentUrl !== pendingDockNavigation.sourceUrl) {
      pendingDockNavigation = null;
    }
  }

  private markPendingNavigation(target: DockPointer, targetUrl: string): void {
    const pending = {
      target,
      targetUrl,
      sourceUrl: NavigationActions.getCurrentBrowserUrl(),
    };
    pendingDockNavigation = pending;
    window.setTimeout(() => {
      if (pendingDockNavigation === pending && NavigationActions.getCurrentBrowserUrl() !== targetUrl) {
        pendingDockNavigation = null;
      }
    }, 1000);
  }

  /** `tab_switch` start line: a navigation that will actually move the app. */
  static logTabSwitchStart(via: string, target: DockPointer | null): void {
    if (!toplog.isOn('tab_switch')) return;
    const sw = beginTabSwitch();
    toplog.log(
      'tab_switch',
      `start sw=${sw} via=${via} from=${dockLabel(NavigationActions.currentBrowserDock())} to=${dockLabel(target) ?? '-'} visibility=${document.visibilityState}`,
    );
  }

  private commitBrowserNavigation(
    target: DockPointer,
    fullUrl: string,
    routerUrl: string,
    opts?: NavigationCommitOptions,
  ): void {
    this.markPendingNavigation(target, fullUrl);

    const from = NavigationActions.getCurrentBrowserUrl();
    const willNavigate = from !== fullUrl;
    toplog.log('navigation', 'commitBrowserNavigation', {
      from,
      to: fullUrl,
      routerUrl,
      willNavigate,
      replace: opts?.replace === true,
      viewModeSwitch: opts?.viewModeSwitch === true,
      historyLen: window.history.length,
    });
    if (willNavigate) {
      NavigationActions.logTabSwitchStart(
        opts?.viewModeSwitch ? 'view_mode' : opts?.replace ? 'replace' : (opts?.via ?? 'openDock'),
        target,
      );
      // React Router owns browser history and, critically, loader execution.
      // A hand-written pushState/popstate updates useLocation but can bypass
      // data-router revalidation, leaving the new URL rendered against stale
      // context. Every dock transition therefore enters through navigate().
      void this.navigate(routerUrl, {
        ...(opts?.replace ? { replace: true } : {}),
        ...(opts?.viewModeSwitch ? { state: VIEW_MODE_SWITCH_STATE } : {}),
      });
    }
  }

  /**
   * Commit a pointer from OUTSIDE the react-router context.
   *
   * The normal path goes through `navigate()` because React Router owns history
   * and, critically, loader execution. A few callers are mounted above the
   * router (the ui_command listener) and have no `navigate` to call, so they
   * push and fire a synthetic popstate for the data router to pick up.
   *
   * That idiom lives HERE rather than being re-spelled per caller — it was
   * already duplicated, with string equality where pointer equality belongs, so
   * a param reordering counted as a different URL and re-pushed.
   */
  static commitDetached(pointer: IDockPointer): void {
    let target = pointer instanceof DockPointer ? pointer : new DockPointer(pointer);
    let here: DockPointer | null = null;
    try {
      here = DockPointer.fromUrl(NavigationActions.getCurrentBrowserUrl());
    } catch {
      here = null;
    }
    // A backend-driven navigate onto workspace content stays in the workspace.
    const carriedHost = hostToCarry(here, target);
    if (carriedHost) target = target.withHost(carriedHost);
    // ...and in the mode on screen, as `openDock` does: otherwise an agent's
    // navigate repaints the user out of the mode they were looking at.
    target = NavigationActions.withTargetViewMode(target, () => here?.viewMode ?? null);
    const url = target.toUrl(window.location.pathname);
    if (here?.equals(target)) return;
    NavigationActions.logTabSwitchStart('detached', target);
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  static resetPendingNavigationForTests(): void {
    pendingDockNavigation = null;
  }

  /**
   * Navigate to the home root with `?highlight=<wikiword>` set, so a home/feed
   * element matching that wiki word renders highlighted. URL-carried (shareable,
   * back-button-safe), mirroring the `selected` option — see docs/wikitip.md.
   * Used by the WikiTip backward link ("click here to highlight the feedentry").
   */
  highlight(wikiword: string): void {
    this.openHomeRoot(wikiword);
  }

  /**
   * Commit a pointer AS-IS — no sticky carry-forward, no scope seeding.
   *
   * The escape hatch for writes that must be able to REMOVE an option
   * (`closeJourney`, `endJourneySteps`) or that are pure param edits on the
   * current surface (`setOption`), where `openDock`'s carry-forward would put
   * back what we are trying to drop. Composition happens on the pointer, so there is
   * no URL string to get stale.
   */
  private commitPointer(dock: DockPointer): void {
    NavigationActions.clearCommittedPendingNavigation();
    const url = dock.toUrl(window.location.pathname);
    if (NavigationActions.getCurrentBrowserUrl() === url) return;
    // `via=commit`: a param edit, a journey close or the dock closing — not a click.
    this.commitBrowserNavigation(dock, url, url, { via: 'commit' });
  }

  /**
   * Leave the journey's step sequence, keeping the journey shown.
   *
   * Lives here beside `showJourney`/`closeJourney` because clearing a STICKY
   * param must bypass `openDock`'s carry-forward — a navigation-layer rule that
   * callers should not have to know, and that a second copy of would drift.
   */
  endJourneySteps(): void {
    this.setOption(JOURNEY_STEP_PARAM, null);
  }

  /**
   * Set (or clear, with `null`) one URL option on the current surface.
   *
   * The typed form of "tweak a param in place" — composed on {@link here}, so a
   * write during an in-flight navigation lands on the destination rather than
   * rebuilding the location we already left. Call sites used `useSearchParams`'s
   * setter, which reads React-state search params and therefore has exactly that
   * staleness.
   */
  setOption(key: string, value: string | null): void {
    this.commitPointer(this.here.withOption(key, value));
  }

  /**
   * Navigate to the app home root `/`, optionally with `?highlight=`.
   *
   * An ordinary `openDock` now: the root is a pointer, so sticky options
   * (journeyId) ride along through the one carry-forward in `openDock` rather
   * than a hand-copied loop here. Also the surface a journey names as
   * `start: {kind: "root"}`.
   */
  private openHomeRoot(highlightWord?: string): void {
    const root = DockPointer.root();
    this.openDock(highlightWord ? root.withHighlight(highlightWord) : root);
  }

  /**
   * "Take me home" — the destination, wherever it is asked from.
   *
   * The two surfaces differ: the hub keeps every navigation under `page=hub`
   * (a desk factory would revert the page and land on the desk home), while the
   * desk home is the app root, which is NOT a dock URL and so needs
   * `openHomeRoot` to carry the sticky options forward. Stated once here so the
   * nav bar — and the next entry point (spotlight, a shortcut, a journey) —
   * can't each re-derive the branch, and so the pending `TODO(nav)` about
   * committing through the router has exactly one place left to land.
   */
  goHome(options?: { homePage?: boolean }): void {
    // Two surfaces, one navigation. The hub keeps every navigation under
    // `page=hub` (a desk factory would revert the page and land on the desk
    // home); the desk home is the app root. Both are pointers, so the branch is
    // a choice of destination rather than a choice of mechanism.
    if (this.here.page === PageId.HUB) {
      this.openDock(DockPointer.forHome().withPage(PageId.HUB));
      return;
    }
    // `homePage`: the Home BUTTON asks for the project's home page — every click,
    // from anywhere — which the root's load redirect resolves
    // (`project-home-page-redirect`). Only the button asks — a fallback "go home"
    // (no chat to resume) wants the plain home.
    this.openDock(options?.homePage ? withHomePage(DockPointer.root()) : DockPointer.root());
  }

  /** The journey shown where we are (or are going), or null. */
  private currentJourneyId(): string | null {
    return this.here.journeyId;
  }

  /**
   * Show a user journey on the CURRENT surface — sets `?journeyId=` on the
   * current dock pointer (or the home root when not on a dock URL). The journey
   * then rides every subsequent navigation via openDock's carry-forward, so it
   * stays topmost until {@link closeJourney}. URL-carried ⇒ reload-safe.
   */
  showJourney(journeyId: string): void {
    // One path for every surface. The old home-root branch hand-built
    // `/?journeyId=…`, which DROPPED every other param on the way — `highlight`
    // included. Composing on the pointer cannot lose the rest of the location.
    this.openDock(this.here.withJourney(journeyId));
  }

  /**
   * Explicitly close the journey — the ONLY thing that clears `journeyId`.
   * Commits the stripped URL directly instead of going through `openDock`,
   * whose carry-forward would immediately put the param back.
   */
  closeJourney(): void {
    if (!this.currentJourneyId()) return;
    // `commitPointer`, not `openDock`: the sticky carry-forward would put the
    // param straight back. This is the one thing that clears it.
    this.commitPointer(this.here.withJourney(null));
  }

  /**
   * Switch the active workspace and resume its last project and tab. `null` (or the default
   * workspace's id) is the default workspace — the param is cleared, so its URLs
   * are the ones from before workspaces existed.
   *
   * `commitPointer`, not `openDock`: the workspace param is sticky, and
   * `openDock`'s carry-forward would put the OLD workspace straight back.
   */
  openWorkspace(workspaceId: string | null): void {
    this.commitPointer(
      DockPointer.root()
        .withOption(WORKSPACE_PARAM, workspaceId || null)
        .withOption(WORKSPACE_ENTRY_PARAM, 'resume'),
    );
  }

  /**
   * Open a project's home INSIDE a workspace — after the project was moved there
   * (`Project.switchWorkspace`). The loader drops a project outside the URL's
   * workspace, so the pointer must name the new one; and like `openWorkspace` this
   * is `commitPointer`, not `openDock`, because the sticky param's carry-forward
   * would put the OLD workspace straight back (`null` = the default workspace).
   */
  openProjectInWorkspace(projectId: string, workspaceId: string | null): void {
    this.commitPointer(DockPointer.forProject(projectId).withOption(WORKSPACE_PARAM, workspaceId || null));
  }

  // ========== Core Navigation ==========

  /**
   * Navigate to a dock pointer (base method)
   * All shortcut methods call this
   *
   * Uses relative navigation: strips dock portion from current URL and appends new dock
   *
   * @param pointer - DockPointer to navigate to, or null to close dock
   */
  openDock(pointer: DockPointer | null, extraOptions?: Record<string, string>, opts?: NavigationCommitOptions): void;
  openDock(pointer: IDockPointer, extraOptions?: Record<string, string>, opts?: NavigationCommitOptions): void;
  openDock(
    pointer: IDockPointer | DockPointer | null,
    extraOptions?: Record<string, string>,
    opts?: NavigationCommitOptions,
  ): void {
    NavigationActions.clearCommittedPendingNavigation();
    const currentPath = window.location.pathname;
    const currentUrl = NavigationActions.getCurrentBrowserUrl();

    toplog.log('navigation', 'openDock', {
      target:
        pointer === null
          ? null
          : `${(pointer as IDockPointer).viewType ?? '?'}:${(pointer as IDockPointer).pointer ?? ''}`,
      currentDock: dockLabel(this.currentDock),
      currentUrl,
      extraOptions,
    });

    if (pointer === null) {
      if (this.currentDock === null) {
        toplog.log('navigation', 'openDock(null) no-op (already not on a dock URL)', { currentUrl });
        return; // already not on a dock URL
      }
      // Closing the dock IS going to the root, and the root is an ordinary
      // pointer — `buildDockUrl` already returns the base path (or `/`) for it,
      // which is exactly what the hand-rolled strip-and-normalize did.
      this.commitPointer(DockPointer.root());
      return;
    }

    const base = pointer instanceof DockPointer ? pointer : new DockPointer(pointer);
    let dock =
      extraOptions && Object.keys(extraOptions).length > 0
        ? new DockPointer(base.viewType, base.pointer, { ...base.options, ...extraOptions }, base.layout, base.page)
        : base;

    // STICKY URL options: each listed param rides onto any target that doesn't
    // set it, read from WHERE WE ARE (or are going) — one carry-forward for
    // every surface now that the home root is an ordinary pointer, where it used
    // to need a hand-copied duplicate in `openHomeRoot`. `journeyId` is the
    // first: a shown journey is TOPMOST until `closeJourney()` (which commits
    // directly so the param can actually clear). New sticky params are one table
    // entry, not another bespoke block.
    const here = this.here;
    for (const key of STICKY_OPTION_PARAMS) {
      const live = here.options?.[key];
      if (live && !dock.options?.[key]) {
        dock = dock.withOption(key, live);
      }
    }

    // The workspace host is sticky the same way, but ONLY onto surfaces that may
    // live inside a workspace — the same predicate that decides whether a tab may
    // adopt a parent. Navigating to a process, a project or a list is a
    // navigation AWAY, and inheriting the host there would resurrect a workspace
    // around a top-level surface. Carrying it from the live URL is what keeps a
    // click inside workspace A in workspace A, rather than following the shown
    // document's last writer into workspace B.
    const carriedHost = opts?.topLevel ? null : hostToCarry(here, dock);
    if (carriedHost) dock = dock.withHost(carriedHost);

    // URL-first default scope for scope-aware surfaces (assets, triggers, file
    // explorer): a dock opened WITHOUT an explicit scope (no `scope-*` keys →
    // `scopeFilter === null`) adopts the current project's scope — project mode
    // when a project is in context (so the minted Tab attaches to that project),
    // else "all". This puts the scope on the URL where the loader and
    // Tab.getFromDockPointer can read it, instead of leaving it to component
    // state (which the tab can't see). An explicit scope (incl. `all`) is
    // respected untouched. This is what makes the left-rail Triggers / Files
    // icons open a project tab when a project is active and a global one
    // otherwise — exactly like the Assets icon.
    if (
      dock.viewType &&
      SCOPE_SEEDED_VIEWS.has(dock.viewType) &&
      dock.scopeFilter === null &&
      !isContentAssetDock(dock)
    ) {
      const projectId = ownerProjectId(dock) ?? dataContext.project?.id ?? null;
      dock = dock.withScopeFilter(projectId ? projectScope(projectId) : allScope());
    }

    // A session entry STATES its mode: its own memory, else the last mode the
    // user selected (see `withTargetViewMode`). Opening only DISPLAYS a mode;
    // memory is minted by `VIEW_MODE_STORE`. Cache-only: a cold deep link has no
    // entity to read here, and the shell loader states a session's mode instead.
    dock = NavigationActions.withTargetViewMode(
      dock,
      () => NavigationActions.currentBrowserViewMode() ?? this.currentDock?.viewMode ?? null,
    );

    if (this.currentDock?.equals(dock)) {
      toplog.log('navigation', 'openDock no-op (currentDock equals target)', {
        dock: dockLabel(dock),
      });
      if (toplog.isOn('tab_switch')) {
        toplog.log('tab_switch', `noop sw=${tabSwitch.id} reason=same_dock to=${dockLabel(dock)}`);
      }
      return; // already at this pointer, no-op
    }

    const layout = preserveWindowLayout(currentPath, dock.layout);
    const targetDock =
      layout === dock.layout ? dock : new DockPointer(dock.viewType, dock.pointer, dock.options, layout, dock.page);
    const fullUrl = targetDock.toUrl(currentPath);

    if (currentUrl === fullUrl || pendingDockNavigation?.targetUrl === fullUrl) {
      toplog.log('navigation', 'openDock no-op (URL already current/pending)', {
        currentUrl,
        fullUrl,
        pending: pendingDockNavigation?.targetUrl ?? null,
      });
      if (toplog.isOn('tab_switch')) {
        toplog.log(
          'tab_switch',
          `noop sw=${tabSwitch.id} reason=${currentUrl === fullUrl ? 'url_current' : 'url_pending'} to=${dockLabel(dock)}`,
        );
      }
      return;
    }

    if (import.meta.env.DEV) {
      try {
        const roundTrippedUrl = DockPointer.fromUrl(fullUrl).toUrl(currentPath);
        if (roundTrippedUrl !== fullUrl) {
          console.error(`DockPointer URL round-trip mismatch: ${fullUrl} -> ${roundTrippedUrl}`);
        }
      } catch (error) {
        console.error(`DockPointer URL round-trip check failed for ${fullUrl}`, error);
      }
    }

    // For react-router with a basename, we need to navigate relative to the base.
    // Extract just the dock portion (everything from /dock/ or /dev/) from the full URL.
    // This allows navigation to work correctly when the app is mounted at a sub-path.
    const basePath = stripDockPortion(currentPath);
    const url = basePath && fullUrl.startsWith(basePath) ? fullUrl.substring(basePath.length) : fullUrl;

    this.commitBrowserNavigation(targetDock, fullUrl, url, opts);
  }

  /**
   * Build the absolute deep-link URL for a dock pointer without navigating.
   * Pure helper — used by share/copy-link flows and openInBrowserTab.
   */
  getDockUrl(pointer: IDockPointer | DockPointer): string {
    const base = pointer instanceof DockPointer ? pointer : new DockPointer(pointer);
    const fullUrl = base.toUrl(window.location.pathname);
    return `${window.location.origin}${fullUrl}`;
  }

  /**
   * Open a dock pointer in a chrome-less `win/` focus window
   * (docs/tab-management.md Part 3 §7): builds the absolute URL with
   * Layout.WIN and opens it via `window.open(url, '_blank')`. On the web
   * that's a new browser tab; inside Electron the main process's
   * setWindowOpenHandler carve-out allows same-origin `/win/` URLs so they
   * open as in-app BrowserWindows.
   */
  openDockInWindow(pointer: IDockPointer | DockPointer): void {
    const base = pointer instanceof DockPointer ? pointer : new DockPointer(pointer);
    const winDock = new DockPointer(base.viewType, base.pointer, base.options, Layout.WIN, base.page);
    window.open(this.getDockUrl(winDock), '_blank');
  }

  /**
   * Close the current dock (navigate to base flow URL)
   */
  closeDock(): void {
    //console.log('[NavigationActions] 🚪 closeDock called');
    this.openDock(null);
    //console.log('[NavigationActions] 🚪 closeDock completed');
  }

  /**
   * Commit a pointer WITHOUT adding a history entry.
   *
   * Same path as {@link openDock} — sticky options, host carry, round-trip check and
   * all — differing only in how it enters history. Exists so the intent reads at the
   * call site: this navigation was not the user's choice, so Back should skip it.
   *
   * The caller is the vibe workspace's active display, where the agent may show a
   * dozen targets in one turn. Pushing each would bury whatever the user was doing a
   * dozen Back presses deep; the show history stays browsable through the display
   * history popover, which is where it belongs.
   */
  replaceDock(pointer: DockPointer | null, extraOptions?: Record<string, string>): void {
    this.openDock(pointer, extraOptions, { replace: true });
  }

  // ========== Tab Navigation (Shortcuts) ==========

  openTab(tabType: ViewType, options?: TabOptions): void {
    const pointer = DockPointer.forTab(tabType, {
      ...(options?.pinned !== undefined && { pinned: options.pinned.toString() }),
      ...(options?.capabilityKind ? { [CAPABILITY_PARAM]: options.capabilityKind } : {}),
    });
    this.openDock(pointer);
  }

  /**
   * Open a view on a specific SPA-surface (page). The hub rail/home use this to
   * keep navigation under `page=hub` — `forTab`/`forHome` are desk-only, so
   * routing a hub view through them would silently revert the page to `desk`.
   */
  openPage(page: PageId, viewType: ViewType = ViewType.HOME, pointer?: string): void {
    this.openDock(new DockPointer(viewType, pointer, undefined, undefined, page));
  }

  // ========== Content Navigation (Shortcuts) ==========

  /**
   * Open the Assets dock at its default surface: the project home when a
   * project is in context (project-home only renders under a project scope),
   * else the global "all" list.
   */
  openAssets(): void {
    const projectId = dataContext.project?.id ?? null;
    this.openDock(
      projectId ? DockPointer.forAssetProjectHome({ scope: projectScope(projectId) }) : DockPointer.forAssetList('all'),
    );
  }

  /**
   * Open the Discover page — a TOP-LEVEL route, not a dock tab — for one
   * project. The project rides as `?scope-project=<id>` (the one URL grammar
   * for "this surface is about project X"); the route's loader adopts it, so
   * a hard load or a bookmark lands on the same state as this click. Without
   * a project the page falls back to the active one.
   */
  openDiscover(projectId?: string | null): void {
    // The scope rides in the URL the way every dock carries it — through
    // `withScopeFilter`, never a hand-built `scope-*` literal.
    const root = DockPointer.root();
    const dock = projectId ? root.withScopeFilter(projectScope(projectId)) : root;
    const query = dock.toSearchParams().toString();
    void this.navigate(query ? `/discover?${query}` : '/discover');
  }

  /** One published asset's page (`/discover/<typeid>`), scoped to its project the same way. */
  openDiscoverAsset(typeid: string, projectId?: string | null): void {
    const root = DockPointer.root();
    const dock = projectId ? root.withScopeFilter(projectScope(projectId)) : root;
    const query = dock.toSearchParams().toString();
    const path = `/discover/${encodeURIComponent(typeid)}`;
    void this.navigate(query ? `${path}?${query}` : path);
  }

  openProject(
    projectId?: string,
    sub?: { roomId?: string | null; tab?: import('@sdk').TypeId | null; sessionId?: string | null },
  ): void {
    this.openDock(DockPointer.forProject(projectId, sub));
  }

  openEditor(path?: string, options?: FileOptions): void {
    const pointer = DockPointer.forFile(path, {
      line: options?.line,
      column: options?.column,
    });
    this.openDock(options?.scope ? pointer.withScopeFilter(options.scope) : pointer);
  }

  /**
   * Open a file with the viewer appropriate for its type: markdown documents
   * in the assets markdown editor (share / chat / rendered view), everything
   * else in the code editor. Thin wrapper over `dockPointerForFile` — the one
   * pointer-level dispatch every "open this file" surface shares.
   */
  openFile(path: string, options?: FileOptions): void {
    this.openDock(dockPointerForFile(path, options));
  }

  /** Resolve a clicked link (terminal output, message text) to the dock that presents it. */
  private async resolveLinkDock(link: string, source: LinkSource | null): Promise<DockPointer> {
    if (!source) throw new Error(t`This link has no source yet`);
    link = localWebUrl(link) ?? link;
    // An app URL copied from this browser is an internal address, not an iframe.
    link = appLinkPath(link, window.location.origin) ?? link;
    const dock = dockForDisplayTarget(await source.resolveDisplayTarget(link));
    if (!dock) throw new Error(t`This link has no available viewer`);
    return dock;
  }

  /**
   * Resolve on activation, then open the plain dock. A user click opens the target
   * in ITS OWN project, never the clicker's: the loader owns placement (it rebases a
   * document onto its owner before the tab is minted), and the backend places the
   * new tab after the active one — the tab the link was clicked in.
   */
  async openLink(link: string, source: LinkSource | null): Promise<void> {
    try {
      this.openDock(await this.resolveLinkDock(link, source));
    } catch (error) {
      notifyLinkError(error);
    }
  }

  /** The link in `process`'s Display — the `flow show` channel, so it lands in the Display history too. */
  async showLinkInDisplay(link: string, process: AgenticProcess): Promise<void> {
    try {
      await process.show({ link: appLinkPath(link, window.location.origin) ?? link });
    } catch (error) {
      notifyLinkError(error);
    }
  }

  /** The link as a tab of `process`'s vibe workspace — the process opens in vibe mode showing it. */
  async openLinkInVibe(link: string, source: LinkSource | null, process: AgenticProcess): Promise<void> {
    try {
      const dock = placeDockInProject(
        await this.resolveLinkDock(link, source),
        process.project_id ?? source?.project_id,
      );
      this.openDock(dock.withHost(process.typeId.toString()).withViewMode(ViewMode.Vibe));
    } catch (error) {
      notifyLinkError(error);
    }
  }

  /** The system browser: a web URL as itself, anything else as the Flowpad view that presents it. */
  async openLinkInBrowser(link: string, source: LinkSource | null): Promise<void> {
    try {
      openExternal(await this.browserUrlFor(link, source));
    } catch (error) {
      notifyLinkError(error);
    }
  }

  /** `openLinkInBrowser`, in one browser profile of this machine (`fetchBrowserProfiles`). */
  async openLinkInBrowserProfile(
    link: string,
    source: LinkSource | null,
    browser: string,
    profile: string,
  ): Promise<void> {
    try {
      await openInBrowserProfile(await this.browserUrlFor(link, source), browser, profile);
    } catch (error) {
      notifyLinkError(error);
    }
  }

  private async browserUrlFor(link: string, source: LinkSource | null): Promise<string> {
    if (isWebUrl(link)) return link;
    // `clau.de/reset` is a web page written without its scheme, not a file to look up.
    const web = bareWebUrl(link);
    if (web?.sure) return web.url;
    try {
      // The plain dock: the loader rebases a document onto its own project (rule 2).
      return this.getDockUrl(await this.resolveLinkDock(link, source));
    } catch (error) {
      if (web) return web.url;
      throw error;
    }
  }

  /**
   * Open a FILE addressed by ABSOLUTE MACHINE path, on a given entity.
   *
   * Dock pointers address files as VFS paths (`compute_node-<id>/abs/path`), so
   * every caller holding a real filesystem path has to convert first — and each
   * one that did it inline was a chance for the two forms to drift. Same job as
   * `openFolder` does for directories; `openFile` remains the VFS-path entry.
   */
  openMachinePath(machinePath: string, typeId: TypeId, options?: FileOptions): void {
    this.openFile(VFSPath.fromMachinePath(machinePath, typeId).rawPath, options);
  }

  /**
   * Open a FOLDER in the Assets fs browser — the folder counterpart of
   * `openFile`. `openFile` would render a directory path as an empty file, so
   * any surface that has a directory (file browsers, task artifacts) routes it
   * here. Converts the absolute machine path to the compute-node-relative form
   * the `fs/` pointer expects (handles POSIX `/…` and Windows `C:\…`).
   * `select` names an entry of the folder to show selected.
   */
  openFolder(machinePath: string, select?: string): void {
    const parsed = VFSPath.parse(machinePath);
    if (parsed.isAbsolute) {
      this.openDock(DockPointer.forAssetFs(parsed, Layout.DOCK, select));
      return;
    }

    const liveTypeId = dataContext.computeNodeTypeId ?? LOCAL_COMPUTE_NODE;
    const locatorTypeId = vfsLocatorForComputeNode(dataContext.computeNode) ?? liveTypeId;
    const vfsPath =
      machinePath.startsWith('/') || /^[A-Za-z]:[/\\]/.test(machinePath)
        ? VFSPath.fromMachinePath(machinePath, locatorTypeId)
        : VFSPath.fromTypeId(locatorTypeId, machinePath);
    this.openDock(DockPointer.forAssetFs(vfsPath, Layout.DOCK, select));
  }

  /** Navigate to the default shell view (no specific session) */
  openShellView(): void {
    this.openDock(new DockPointerData(ViewType.SHELL));
  }

  async openShell(
    shellId: string,
    options?: {
      cwd?: string;
      /** Typed AND submitted once the terminal attaches. */
      startCommand?: string;
      skipPermissions?: boolean;
      viewMode?: string;
      host?: string;
      /** A top-level tab, never a child of the workspace on screen (see `NavigationCommitOptions.topLevel`). */
      topLevel?: boolean;
    },
  ): Promise<Shell | null> {
    const { topLevel, ...urlOptions } = options ?? {};
    const extraOptions = toStringRecord(urlOptions);
    const shell = Shell.getByIdFromCache(shellId) ?? (await Shell.getById(shellId));
    if (!shell) {
      return null;
    }
    // On the hub a terminal lives on the hub page (there is no desk to host it).
    this.openDock(
      isHubOnly()
        ? new DockPointer(
            ViewType.SHELL,
            shell.typeId.toString(),
            shell.compute_node_id ? { [NODE_PARAM]: shell.compute_node_id } : undefined,
            undefined,
            PageId.HUB,
          )
        : shell.dockPointer,
      extraOptions,
      topLevel ? { topLevel: true } : undefined,
    );
    return shell;
  }

  /** Start through the existing Vibe launcher, keeping an open file as a child. */
  async startVibeSession(projectId: string, workdir?: string): Promise<void> {
    const here = this.here;
    const contentDock = here && isContentAssetDock(here) ? here : null;
    const { createVibeProcessForProject } = await import('@src/pages/flow-page/use-start-vibe-session');
    await createVibeProcessForProject({
      projectId,
      workdir,
      navigation: {
        openShellProcess: (processId) => {
          if (contentDock) {
            this.openDock(hostedAssetDock(contentDock, projectId, processId));
          } else {
            void this.openShellProcess(processId, { viewMode: ViewMode.Vibe });
          }
        },
      },
    });
  }

  /**
   * Show a session on another SURFACE — Vibe, Chat or Terminal. The same switch as
   * the session's own switch makes (marked `viewModeSwitch`, so the mode is recorded):
   * one tab, re-pointed; the transport follows (`useProcessSurface`). Vibe → its
   * host dock; leaving Vibe carries the nested tabs out (backend re-point).
   */
  switchSessionSurface(processId: string, mode: ViewMode): void {
    this.openDock(DockPointer.forSession(processId).withViewMode(mode), undefined, { viewModeSwitch: true });
  }

  /**
   * Open `processId` as a Vibe tab placed RIGHT AFTER the tab on screen (or after
   * its host, from a child) — the chat header's New / Recent. A session reopened
   * this way brings back the children that closed with it (backend reopen path).
   */
  async openVibeTabBeside(processId: string): Promise<void> {
    this.openDock(await this.placeVibeTabBeside(processId), undefined, { topLevel: true });
  }

  /** Mint (or reopen) `processId`'s Vibe tab right after the tab on screen — or
   *  after its host, from a child — without navigating. */
  private async placeVibeTabBeside(processId: string): Promise<DockPointer> {
    // The snapshot: the tab on screen is already in it (no round trip before the open).
    const tabs = await tabManager.snapshotOrRefresh();
    const here = tabForDockKey(tabs, this.here.tabHash ?? '');
    const anchor = (here?.parent_tab_id && tabs.find((t) => t.id === here.parent_tab_id)) || here;
    const dock = DockPointer.forSession(processId).withViewMode(ViewMode.Vibe);
    return presentDockTab(dock, { afterTabId: anchor?.id ?? null, projectId: anchor?.project_id ?? null });
  }

  /**
   * THE way into "chat with this doc": a Vibe HOST tab with the doc as its child,
   * the chat beside it. Resumes the latest chat about the doc (`target_typeid_str`
   * = the doc) in the place it runs, and creates one only when there is none.
   * The place: the doc tab's project → the active project → no project (the
   * backend's chat-scratch folder). Viewers that have no chat-beside mode
   * (`offersDocChat`) are a no-op.
   */
  async enterChat(asset: DockPointer): Promise<void> {
    if (!offersDocChat(asset)) return;
    const [tabs, { lastVibeChatQuery, pickLastVibeChat }, { createVibeProcessForProject }] = await Promise.all([
      tabManager.snapshotOrRefresh(),
      import('@src/pages/flow-page/vibe-process-resolver'),
      import('@src/pages/flow-page/use-start-vibe-session'),
    ]);
    const projectId = tabForDockKey(tabs, asset.tabHash ?? '')?.project_id ?? dataContext.project?.id ?? null;
    const target = contentAssetTargetForDock(asset)?.targetVfsPath;
    const last = target
      ? pickLastVibeChat(
          await AgenticProcess.query<AgenticProcess>(lastVibeChatQuery(projectId, target, { openedOnly: false })),
        )
      : null;
    const processId =
      last?.id ?? (await createVibeProcessForProject({ projectId, targetVfsPath: target, open: false })).id;
    await this.openDocInChat(asset, processId, projectId);
  }

  /** `asset` beside the chat `processId` — the doc as a child of that chat's Vibe host
   *  tab. `enterChat` ends here; the chat's doc chip comes straight here. */
  async openDocInChat(asset: DockPointer, processId: string, projectId: string | null): Promise<void> {
    await this.placeVibeTabBeside(processId);
    this.openDock(hostedAssetDock(asset, projectId, processId));
  }

  /** Out of "chat with this doc": the same doc, plain. The chat keeps running (Recent, the chat chip). */
  leaveChat(asset: DockPointer): void {
    // `topLevel`: otherwise `hostToCarry` puts the host we are leaving straight back.
    this.openDock(asset.withHost(null).withViewMode(null), undefined, { topLevel: true });
  }

  async openShellProcess(
    agenticProcessId: string,
    options?: { t?: string; windows?: string; activeWindow?: string; viewMode?: string },
  ): Promise<AgenticProcess | null> {
    const extraOptions = toStringRecord(options);
    const process =
      AgenticProcess.getByIdFromCache(agenticProcessId) ?? (await AgenticProcess.getById(agenticProcessId));
    if (!process) {
      return null;
    }
    // ONE surface per process, whatever the mode: the shell dock. Vibe is a
    // rendering mode of that same tab — `extraOptions.viewMode` rides the URL
    // as `?viewMode=vibe` via the openDock options merge, never a second URL
    // family (the display tab identity is gone; legacy /dock/display URLs
    // redirect in canonicalProcessDockPath).
    this.openDock(process.terminalDockPointer, extraOptions);
    return process;
  }

  /**
   * Navigate to any entity by its DockPointerData.
   * Prefer this over openShellProcess when you already have a process object.
   */
  openDockPointer(pointer: IDockPointer, options?: Record<string, string>): void {
    this.openDock(pointer, options);
  }

  /** Open an AgenticProcess in a terminal tab. Route loader calls open(). */
  async openProcessTab(processId: string, options?: Record<string, string>): Promise<void> {
    const process = AgenticProcess.getByIdFromCache(processId) ?? (await AgenticProcess.getById(processId));
    if (!process) return;
    this.openDock(process.terminalDockPointer, options);
  }

  /** Open a plain Shell in a terminal tab. */
  async openShellTab(shellId: string): Promise<void> {
    const shell = Shell.getByIdFromCache(shellId) ?? (await Shell.getById(shellId));
    if (!shell) return;
    this.openDock(shell.dockPointer);
  }

  /**
   * Resolve a worker/session/thread id and navigate to it.
   * Backend auto-discovers worker_type. Returns null when the id is unknown
   * to worker history; caller is expected to surface a toast.
   */
  async openWorkerSession(workerId: string): Promise<AgenticProcess | null> {
    const process = await AgenticProcess.getByWorkerId(workerId).catch((err) => {
      console.error('[NavigationActions.openWorkerSession]', err);
      return null;
    });
    if (!process) return null;
    this.openDock(process.dockPointer);
    return process;
  }

  /**
   * Open a shell by ID, automatically detecting whether it has a linked
   * AgenticProcess and navigating to the correct URL.
   * Use this when you have a shellId but don't know if it's a plain shell or a Claude session.
   */
  async openSession(shellId: string, options?: { skipPermissions?: boolean }): Promise<void> {
    // The server finds the shell's process — not every process brought here to look through.
    const processes = await AgenticProcess.query<AgenticProcess>(
      new QueryRequest({
        type: AgenticProcess.type,
        scope: [],
        query: new QueryFilter({ match: { shell_id: shellId }, limit: 1 }),
      }),
    );
    const linkedProcess = processes.find((p) => p.shell_id === shellId);
    if (linkedProcess) {
      await this.openShellProcess(linkedProcess.id);
    } else {
      await this.openShell(shellId, options);
    }
  }

  async openNewShell(options?: {
    cwd?: string;
    /** Typed AND submitted once the terminal attaches. */
    startCommand?: string;
    computeNode?: ComputeNode;
    skipNavigate?: boolean;
    projectId?: string;
    /** Open the terminal in this view mode (`vibe` keeps a journey in its skin). */
    viewMode?: string;
  }): Promise<{ shellId: string } | null> {
    try {
      const cn = options?.computeNode ?? dataContext.computeNode;
      if (!cn) {
        console.error('[NavigationActions] No compute node');
        if (!options?.skipNavigate) this.openShellView();
        return null;
      }
      const { nextTerminalName } = await import('@src/components/terminal/rename-rules');
      const shells = await Shell.list(cn.id);
      const name = nextTerminalName(shells.map((s) => ({ name: s.name ?? '' })));
      // For sandbox compute nodes the project's host path is meaningless;
      // fall back to the node's own mount path (e.g. /home/user) instead.
      const cwd =
        options?.cwd ||
        (options?.computeNode
          ? (options.computeNode.fs_storage_mount_path ?? undefined)
          : dataContext.project?.fs_storage_mount_path) ||
        undefined;
      const newShell = Shell.create(cn, { name, workdir: cwd });
      // Project consolidation (Path A, 2026-05-09): every Shell carries a
      // real ``project_id``. Prefer the caller-pinned project, then the
      // active dock project; the backend's Shell.save defaults to the
      // bootstrap ``@local`` project if both are absent.
      const pinnedProjectId = options?.projectId ?? dataContext.project?.id ?? null;
      if (pinnedProjectId) newShell.project_id = pinnedProjectId;
      // On the hub a terminal is a remote PTY session on the node, never a Shell row:
      // the shell lives in the client cache and starts by its id (Shell.start).
      if (!isHubOnly()) await newShell.save(cn.typeId);
      if (!options?.skipNavigate) {
        await this.openShell(newShell.id, options);
      }
      return { shellId: newShell.id };
    } catch (error) {
      console.error('[NavigationActions] Error creating terminal:', error);
      if (!options?.skipNavigate) this.openShellView();
      return null;
    }
  }

  /**
   * Open the plan viewer for a plan file. Addresses the plan by its **VFS path**
   * (race-free — independent of the fs-records scanner having minted the PLAN
   * entity, and of the owning process still being alive). The originating
   * process — needed only for Execute/Update — is read from the current process
   * context entity, not the URL. `agenticProcessTypeId` is retained in the
   * signature so the live call sites stay unchanged; it is intentionally unused.
   * @param _agenticProcessTypeId - (unused) TypeId of the owning AgenticProcess
   * @param filePath - Absolute path to plan .md file
   */
  openPlan(_agenticProcessTypeId: TypeId, filePath: string): void {
    this.openDock(DockPointer.forPlanByPath(filePath));
  }

  openDiff(checkpointHash: string): void {
    const pointer = DockPointer.forCheckpoint(checkpointHash);
    this.openDock(pointer);
  }

  /**
   * Open System Profile view with optional tab and item
   * URL structure: /dock/system_profile/<tab>?item=<item>&scope=<scope>&project=<project>
   *
   * @param tab - Tab name (e.g., "summary", "projects", "sessions", "transcripts")
   * @param item - Optional item within the tab
   * @param options - Optional scope filter options
   * @param options.scope - Scope filter: 'all' | 'global' | 'project'
   * @param options.project - Project encoded name (when scope is 'project')
   */
  openSystemProfile(tab?: string, item?: string, options?: { scope?: string; project?: string }): void {
    const pointer = DockPointer.forSystemProfile(tab, item, options);
    this.openDock(pointer);
  }

  // ========== Lens Navigation ==========

  /**
   * Open lens viewer
   * @param category - Lens category (e.g., "claude")
   * @param type - Lens type (e.g., "transcript", "tasks")
   * @param ref - Reference (e.g., session ID, "projectHash/sessionId")
   * @param options - Optional query params
   */
  openLens(category: string, type: string, ref: string, options?: Record<string, string>): void {
    const pointer = DockPointer.forLens(category, type, ref, undefined, options);
    this.openDock(pointer);
  }

  /** Open the table behind one home counter (see `DockPointer.forCounterAssets`). */
  openAssetList(group: string, counter: string): void {
    this.openDockOnThisPage(DockPointer.forCounterAssets(group, counter));
  }

  /** Open a desk pointer on the page we are on: on the hub a bare desk pointer would revert to hub home. */
  openDockOnThisPage(pointer: IDockPointer | DockPointer): void {
    const dock = pointer instanceof DockPointer ? pointer : new DockPointer(pointer);
    this.openDock(this.here.page === PageId.HUB ? dock.withPage(PageId.HUB) : dock);
  }

  /**
   * Open record search view
   * @param query - Optional initial query string
   * @param filters - Optional filter options
   */
  openSearch(
    query?: string,
    filters?: {
      record_type?: string;
      status?: string;
      scope?: string;
      time_preset?: string;
      time_start?: string;
      time_end?: string;
    },
  ): void {
    const pointer = DockPointer.forSearch(query, filters);
    this.openDock(pointer);
  }

  /**
   * Open the user Preferences screen
   * @param category - Optional category whose tab should be active
   */
  openPreferences(category?: string): void {
    const pointer = DockPointer.forPreferences(category);
    this.openDock(pointer);
  }

  /**
   * Open the Credentials screen — the one way in from every surface.
   * @param tab - Which tab is active; defaults to Connections
   * @param projectId - Project whose credentials and env files are shown; defaults to the
   *   active project. Decided HERE, at navigation time, and written into the URL: the view
   *   itself never guesses a project the URL does not name.
   */
  openCredentials(tab?: CredentialsSubview, projectId: string | undefined = dataContext.project?.id): void {
    this.openDock(DockPointer.forCredentials(tab, projectId));
  }

  // ========== History Navigation ==========

  /**
   * Step back one entry — but only if there IS one. Unguarded, `navigate(-1)`
   * on a freshly deep-linked page walks out of the app entirely (to whatever
   * the tab showed before, or a blank page in the Electron shell).
   *
   * This is the HISTORY affordance and nothing else. A view that wants "leave
   * this thing" should name its destination — `openDock(DockPointer.forX())` —
   * rather than depend on where the user happened to come from.
   */
  goBack(): void {
    const { canGoBack, idx } = getHistoryPosition();
    toplog.log('navigation', 'NavigationActions.goBack', {
      canGoBack,
      idx,
      currentUrl: NavigationActions.getCurrentBrowserUrl(),
      currentDock: dockLabel(this.currentDock),
    });
    if (!canGoBack) return;
    void this.navigate(-1);
  }

  goForward(): void {
    const { canGoForward, idx } = getHistoryPosition();
    toplog.log('navigation', 'NavigationActions.goForward', {
      canGoForward,
      idx,
      currentUrl: NavigationActions.getCurrentBrowserUrl(),
    });
    if (!canGoForward) return;
    void this.navigate(1);
  }
}
